import type { CommonResult, HttpRequest } from '@zoryn/network'
import type { MessageSummaryStats } from '../types'
import { DEFAULT_MESSAGE_API_PREFIX } from '../constants'

export interface ImConversationDto {
  id: number | string
  kind: string
  title: string
  subtitle?: string
  online?: boolean
  starred?: boolean
  muted?: boolean
  pinned?: boolean
  pinnedAt?: string | number
  labels?: string[]
  lastPreview?: string
  lastTime?: string | number
  unread?: number
  type?: string
  sender?: string
  bizTab?: string
  payload?: Record<string, unknown>
  members?: Array<{
    id: string
    name: string
    kind?: string
    username?: string
    email?: string
  }>
  botCode?: string
}

export interface ImMessageDto {
  id: number | string
  conversationId: number | string
  from?: string
  kind?: string
  content?: string
  createTime?: string | number
  senderName?: string
  fileName?: string
  read?: boolean
  payload?: Record<string, unknown>
}

export type ImRealtimeOp =
  | 'new'
  | 'read'
  | 'unread'
  | 'star'
  | 'mute'
  | 'pin'
  | 'labels'
  | 'hide'
  | 'clear'
  | 'delete'

export interface ImRealtimeEvent {
  op: ImRealtimeOp
  conversationId?: string
  unreadTotal?: number
  conversation?: ImConversationDto
  message?: ImMessageDto
}

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>
    if (Array.isArray(record.data)) return record.data as T[]
    if (Array.isArray(record.list)) return record.list as T[]
  }
  return []
}

function unwrapData<T>(body: unknown, fallback: T): T {
  if (body && typeof body === 'object' && 'data' in (body as object)) {
    const data = (body as CommonResult<T>).data
    return (data ?? fallback) as T
  }
  return (body as T) ?? fallback
}

export function createMessageApi(
  http: HttpRequest,
  apiPrefix = DEFAULT_MESSAGE_API_PREFIX
) {
  async function fetchConversations(params?: { keyword?: string; unread?: boolean; kind?: string }) {
    const body = await http.get({
      url: `${apiPrefix}/conversations`,
      params,
    })
    return unwrapList<ImConversationDto>(body)
  }

  async function fetchMessages(conversationId: string, cursor?: string) {
    const body = await http.get({
      url: `${apiPrefix}/conversations/${conversationId}/messages`,
      params: cursor ? { cursor } : undefined,
    })
    return unwrapList<ImMessageDto>(body)
  }

  async function sendMessage(
    conversationId: string,
    payload: { contentType?: string; content: string; fileName?: string }
  ) {
    const body = await http.post({
      url: `${apiPrefix}/conversations/${conversationId}/messages`,
      params: payload,
    })
    return unwrapData<ImMessageDto | null>(body, null)
  }

  async function createConversation(
    payload: { kind?: string; userIds: number[]; title?: string },
    options?: { skipErrorHandler?: boolean }
  ) {
    const body = await http.post({
      url: `${apiPrefix}/conversations`,
      params: payload,
      meta: options?.skipErrorHandler ? { skipErrorHandler: true } : undefined,
    })
    return unwrapData<ImConversationDto | null>(body, null)
  }

  async function markConversationRead(id: string) {
    await http.put({ url: `${apiPrefix}/conversations/${id}/read` })
  }

  async function markAllReadApi() {
    await http.put({ url: `${apiPrefix}/read-all` })
  }

  async function starConversation(id: string, starred?: boolean) {
    await http.put({
      url: `${apiPrefix}/conversations/${id}/star`,
      params: starred == null ? {} : { starred },
    })
  }

  async function markConversationUnread(id: string) {
    await http.put({ url: `${apiPrefix}/conversations/${id}/unread` })
  }

  async function muteConversation(id: string, muted?: boolean) {
    await http.put({
      url: `${apiPrefix}/conversations/${id}/mute`,
      params: muted == null ? {} : { muted },
    })
  }

  async function pinConversation(id: string, pinned?: boolean) {
    await http.put({
      url: `${apiPrefix}/conversations/${id}/pin`,
      params: pinned == null ? {} : { pinned },
    })
  }

  async function updateConversationLabels(id: string, labels: string[]) {
    await http.put({
      url: `${apiPrefix}/conversations/${id}/labels`,
      params: { labels },
    })
  }

  async function hideConversation(id: string) {
    await http.put({ url: `${apiPrefix}/conversations/${id}/hide` })
  }

  async function clearConversationMessages(id: string) {
    await http.del({ url: `${apiPrefix}/conversations/${id}/messages` })
  }

  async function deleteConversation(id: string) {
    await http.del({ url: `${apiPrefix}/conversations/${id}` })
  }

  async function fetchUnreadCount() {
    const body = await http.get({ url: `${apiPrefix}/unread-count` })
    const count = unwrapData<number>(body, 0)
    return typeof count === 'number' ? count : 0
  }

  async function fetchSummary() {
    const body = await http.get<CommonResult<MessageSummaryStats>>({
      url: `${apiPrefix}/summary`,
    })
    return unwrapData<MessageSummaryStats>(body, {
      unread: 0,
      system: 0,
      business: 0,
      alert: 0,
    })
  }

  return {
    fetchConversations,
    fetchMessages,
    sendMessage,
    createConversation,
    markConversationRead,
    markAllReadApi,
    starConversation,
    markConversationUnread,
    muteConversation,
    pinConversation,
    updateConversationLabels,
    hideConversation,
    clearConversationMessages,
    deleteConversation,
    fetchUnreadCount,
    fetchSummary,
  }
}

export type MessageApi = ReturnType<typeof createMessageApi>
